// Single source of truth for navigation.
//
// Every screen must be reachable by URL, survive a refresh, and respond to the
// browser Back button. Previously each component hand-rolled its own
// `currentPage.set(...)` + `history.pushState(...)` pair, which meant some
// transitions pushed twice, some never pushed at all, and the whole
// face-analysis flow had no URL — so Back exited the site and took the user's
// analysis with it.
//
// Import `navigate` and use it for every page change.

import { currentPage } from './store.js';

// URL -> page name
export const PATH_TO_PAGE = {
	'/': 'home',
	'/my-skin': 'face-analysis-result',
	'/face-analysis': 'face-analysis',
	'/face-analysis-result': 'face-analysis-result',
	'/about-your-skin': 'skin-questions',
	'/recommendations': 'recommendations',
	'/routine': 'routine',
	'/routine/import': 'routine-import',
	'/next': 'next-step',
	'/scanner': 'scanner',
	'/progress': 'progress',
	'/profile': 'profile',
	'/token-received': 'token-received',
	'/privacy': 'privacy',
	'/terms': 'terms',
	'/delete-account': 'delete-account'
};

// page name -> canonical URL
export const PAGE_TO_PATH = {
	'home': '/',
	'face-analysis': '/face-analysis',
	'face-analysis-result': '/my-skin',
	'skin-questions': '/about-your-skin',
	'recommendations': '/recommendations',
	'routine': '/routine',
	'routine-import': '/routine/import',
	'next-step': '/next',
	'scanner': '/scanner',
	'progress': '/progress',
	'profile': '/profile',
	'privacy': '/privacy',
	'terms': '/terms',
	'delete-account': '/delete-account'
};

// Pages that genuinely cannot render without a session. Everything else must
// work signed out — losing your analysis on refresh is worse than showing a
// page with an empty state.
// 'delete-account' is deliberately NOT here. A signed-out visitor has rows
// on the server too, and the one screen that deletes them must not be the
// screen that bounces them to the homepage.
export const AUTH_ONLY_PAGES = ['profile'];

export function pageFromPath() {
	const path = window.location.pathname.replace(/\/+$/, '') || '/';
	// An unknown path used to fall back to 'home', so every mistyped or stale
	// link rendered the landing page at whatever address had been typed. That
	// is how the footer could link /privacy for a month without anything
	// looking broken: the policy did not exist and the homepage stood in for
	// it, silently.
	return PATH_TO_PAGE[path] || 'not-found';
}

export function pathForPage(page) {
	return PAGE_TO_PATH[page] || '/';
}

// 'not-found' has no canonical path on purpose: it keeps whatever address the
// person actually typed, so they can see the typo.


/**
 * Change page. Always use this instead of setting `currentPage` directly, so
 * the store and the URL can never drift apart.
 *
 * @param {string} page                     page name from PAGE_TO_PATH
 * @param {object} [options]
 * @param {boolean} [options.replace]       replace the history entry instead of pushing
 * @param {boolean} [options.scroll=true]   scroll to top after navigating
 */
export function navigate(page, { replace = false, scroll = true } = {}) {
	currentPage.set(page);

	const path = pathForPage(page);
	if (replace) {
		history.replaceState({}, '', path);
	} else if (window.location.pathname !== path) {
		history.pushState({}, '', path);
	}

	if (scroll && typeof window !== 'undefined') {
		window.scrollTo(0, 0);
	}
}
